import React, { useEffect, useState } from 'react';
import { useMarkets } from '../hooks/useMarkets';
import { useDisputes } from '../hooks/useDisputes';
import DisputeButton from '../components/DisputeButton';
import OracleConsensusTable from '../components/OracleConsensusTable';
import MarketCycleStatus from '../components/MarketCycleStatus';
import { Market } from '../hooks/usePredictionMarket';

/**
 * DisputesPage
 *
 * Lists all resolved markets with dispute-raising capability and
 * shows oracle consensus data for markets under review.
 */
export default function DisputesPage() {
  const { markets, loadingMarkets, refetch } = useMarkets();
  const { disputes, fetchDispute, isConfigured } = useDisputes();
  const [selectedMarket, setSelectedMarket] = useState<Market | null>(null);

  // Only show resolved markets on this page
  const resolvedMarkets = markets.filter(m => m.resolved);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Pre-fetch dispute state for all resolved markets once loaded
  useEffect(() => {
    if (!isConfigured) return;
    resolvedMarkets.forEach(m => fetchDispute(m.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markets.length, isConfigured]);

  function getDisputeInfo(marketId: number) {
    return disputes.find(d => d.marketId === marketId) ?? null;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h2 className="page-title">⚠️ Disputes</h2>
        <p className="page-subtitle">
          Review settled markets, raise challenges, and monitor oracle consensus.
          {!isConfigured && (
            <span className="config-warning">
              {' '}(Multi-oracle contract not configured – disputes unavailable)
            </span>
          )}
        </p>
      </div>

      <button
        className="btn-outline"
        onClick={refetch}
        disabled={loadingMarkets}
        style={{ marginBottom: 16 }}
      >
        {loadingMarkets ? '⟳ Loading…' : '⟳ Refresh'}
      </button>

      {loadingMarkets ? (
        <div className="loading-state">Loading markets…</div>
      ) : resolvedMarkets.length === 0 ? (
        <div className="empty-state">No resolved markets yet.</div>
      ) : (
        <div className="disputes-list">
          {resolvedMarkets.map(market => {
            const disputeInfo = getDisputeInfo(market.id);
            const isDisputed  = disputeInfo?.disputed ?? false;

            return (
              <div
                key={market.id}
                className={`dispute-card${isDisputed ? ' dispute-card-disputed' : ''}`}
              >
                <div className="dispute-card-header">
                  <span className="market-id">#{market.id}</span>
                  <MarketCycleStatus market={market} compact />
                  {isDisputed && <span className="badge badge-expired">⚠️ Disputed</span>}
                </div>

                <p className="market-question">{market.question}</p>

                {disputeInfo && (
                  <div className="dispute-votes-summary">
                    <span>YES votes: {disputeInfo.yesVotes}</span>
                    <span>NO votes:  {disputeInfo.noVotes}</span>
                    {disputeInfo.resolvedAt > 0 && (
                      <span>
                        Resolved: {new Date(disputeInfo.resolvedAt * 1000).toLocaleString()}
                      </span>
                    )}
                  </div>
                )}

                <div className="dispute-actions">
                  <button
                    className="btn-outline"
                    onClick={() =>
                      setSelectedMarket(selectedMarket?.id === market.id ? null : market)
                    }
                  >
                    {selectedMarket?.id === market.id ? 'Hide Consensus' : 'View Consensus'}
                  </button>
                  <DisputeButton
                    marketId={market.id}
                    disabled={isDisputed}
                    onSuccess={() => { fetchDispute(market.id); }}
                  />
                </div>

                {selectedMarket?.id === market.id && (
                  <OracleConsensusTable marketId={market.id} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
